import { create } from 'zustand'
import { persist, subscribeWithSelector } from 'zustand/middleware'
import type { CustomerCard } from '@/types/dashboard'
import { supabase } from '@/lib/supabase'

export interface GroupBox {
  id: string
  label: string
  position: { x: number; y: number }
  size: { width: number; height: number }
  color: string
}

interface CustomerTrackerState {
  customers: CustomerCard[]
  groupBoxes: GroupBox[]
  drawMode: boolean
  addCustomer: (userId: string, name: string) => void
  duplicateCustomer: (id: string) => void
  updatePosition: (id: string, position: { x: number; y: number }) => void
  updateSize: (id: string, size: { width: number }) => void
  toggleFollowUp: (id: string) => void
  removeCustomer: (id: string) => void
  updateDescription: (id: string, description: string) => void
  addTimelineEntry: (id: string, date: string, note: string, type: 'past' | 'future') => void
  removeTimelineEntry: (customerId: string, entryId: string) => void
  addGroupBox: (label: string, position: { x: number; y: number }, size: { width: number; height: number }, color: string) => void
  updateGroupBox: (id: string, updates: Partial<GroupBox>) => void
  removeGroupBox: (id: string) => void
  toggleDrawMode: () => void
}

// Debounced sync to Supabase
let customerSyncTimeout: NodeJS.Timeout | null = null
const debouncedSyncCustomers = async (customers: CustomerCard[]) => {
  if (customerSyncTimeout) clearTimeout(customerSyncTimeout)

  customerSyncTimeout = setTimeout(async () => {
    try {
      // Upsert customers
      const customersToUpsert = customers.map((customer) => {
        const size = customer.size || { width: 300 }
        return {
          id: customer.id,
          user_id: customer.userId || null,
          name: customer.name,
          position_x: Math.round(customer.position.x),
          position_y: Math.round(customer.position.y),
          card_width: Math.round(size.width),
          card_height: null, // Height is now auto-calculated
          current_description: customer.currentDescription,
          needs_follow_up: customer.needsFollowUp,
          updated_at: customer.lastUpdated,
        }
      })

      const { error: customerError } = await supabase
        .from('office_tv_customers')
        .upsert(customersToUpsert, { onConflict: 'id' })

      if (customerError) {
        console.error('Error syncing customers:', customerError)
        // If columns don't exist, just skip sync (migration not run yet)
        if (customerError.message?.includes('column') || customerError.code === '42703') {
          console.warn('Position columns not found in database. Run SQL migration first.')
          return
        }
      }

      // Upsert timeline entries for all customers
      const allTimelineEntries = customers.flatMap((customer) =>
        (customer.timeline || []).map((entry) => ({
          id: entry.id,
          customer_id: customer.id,
          date: entry.date,
          note: entry.note,
          type: entry.type,
          created_at: entry.createdAt,
        }))
      )

      if (allTimelineEntries.length > 0) {
        await supabase
          .from('office_tv_customer_timeline')
          .upsert(allTimelineEntries, { onConflict: 'id' })
      }
    } catch (error) {
      console.error('Error syncing customers:', error)
    }
  }, 1000) // 1 second debounce
}

export const useCustomerTrackerStore = create<CustomerTrackerState>()(
  subscribeWithSelector(
    persist(
      (set) => ({
        customers: [],
        groupBoxes: [],
        drawMode: false,

        addCustomer: (userId: string, name: string) =>
          set((state) => ({
            customers: [
              ...state.customers,
              {
                id: crypto.randomUUID(),
                userId,
                name,
                position: { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 },
                size: { width: 300 },
                needsFollowUp: false,
                lastUpdated: new Date().toISOString(),
                currentDescription: '',
                timeline: [],
              },
            ],
          })),

        duplicateCustomer: (id: string) =>
          set((state) => {
            const original = state.customers.find((c) => c.id === id)
            if (!original) return state

            // Find the root customer (in case we're duplicating a copy)
            const rootId = original.linkedTo || original.id

            return {
              customers: [
                ...state.customers,
                {
                  id: crypto.randomUUID(),
                  userId: original.userId,
                  name: original.name,
                  position: { x: original.position.x + 20, y: original.position.y + 20 },
                  size: original.size,
                  needsFollowUp: original.needsFollowUp,
                  lastUpdated: new Date().toISOString(),
                  currentDescription: original.currentDescription,
                  timeline: [],
                  linkedTo: rootId,
                },
              ],
            }
          }),

        updatePosition: (id: string, position: { x: number; y: number }) =>
          set((state) => ({
            customers: state.customers.map((c) => (c.id === id ? { ...c, position, lastUpdated: new Date().toISOString() } : c)),
          })),

        updateSize: (id: string, size: { width: number; height?: number }) =>
          set((state) => ({
            customers: state.customers.map((c) => (c.id === id ? { ...c, size: { width: size.width }, lastUpdated: new Date().toISOString() } : c)),
          })),

      toggleFollowUp: (id: string) =>
        set((state) => ({
          customers: state.customers.map((c) => (c.id === id ? { ...c, needsFollowUp: !c.needsFollowUp, lastUpdated: new Date().toISOString() } : c)),
        })),

      removeCustomer: (id: string) =>
        set((state) => ({
          customers: state.customers.filter((c) => c.id !== id),
        })),

      updateDescription: (id: string, description: string) =>
        set((state) => ({
          customers: state.customers.map((c) => (c.id === id ? { ...c, currentDescription: description, lastUpdated: new Date().toISOString() } : c)),
        })),

      addTimelineEntry: (id: string, date: string, note: string, type: 'past' | 'future') =>
        set((state) => ({
          customers: state.customers.map((c) =>
            c.id === id
              ? {
                  ...c,
                  timeline: [
                    ...(c.timeline || []),
                    {
                      id: crypto.randomUUID(),
                      date,
                      note,
                      type,
                      createdAt: new Date().toISOString(),
                    },
                  ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
                  lastUpdated: new Date().toISOString(),
                }
              : c
          ),
        })),

      removeTimelineEntry: (customerId: string, entryId: string) =>
        set((state) => ({
          customers: state.customers.map((c) => (c.id === customerId ? { ...c, timeline: c.timeline.filter((e) => e.id !== entryId), lastUpdated: new Date().toISOString() } : c)),
        })),

        addGroupBox: (label: string, position: { x: number; y: number }, size: { width: number; height: number }, color: string) =>
          set((state) => ({
            groupBoxes: [
              ...state.groupBoxes,
              {
                id: crypto.randomUUID(),
                label,
                position,
                size,
                color,
              },
            ],
          })),

        updateGroupBox: (id: string, updates: Partial<GroupBox>) =>
          set((state) => ({
            groupBoxes: state.groupBoxes.map((box) => (box.id === id ? { ...box, ...updates } : box)),
          })),

        removeGroupBox: (id: string) =>
          set((state) => ({
            groupBoxes: state.groupBoxes.filter((box) => box.id !== id),
          })),

        toggleDrawMode: () =>
          set((state) => ({
            drawMode: !state.drawMode,
          })),
      }),
      {
        name: 'customer-tracker-store',
        // Migrate existing customers to add size property
        onRehydrateStorage: () => (state) => {
          if (state) {
            const updatedCustomers = state.customers.map((customer) => ({
              ...customer,
              size: customer.size ? { width: customer.size.width } : { width: 300 },
            }))
            state.customers = updatedCustomers
          }
        },
      }
    )
  )
)

// Debounced sync for group boxes
let groupBoxSyncTimeout: NodeJS.Timeout | null = null
const debouncedSyncGroupBoxes = async (groupBoxes: GroupBox[]) => {
  if (groupBoxSyncTimeout) clearTimeout(groupBoxSyncTimeout)

  groupBoxSyncTimeout = setTimeout(async () => {
    try {
      const groupBoxesToUpsert = groupBoxes.map((box) => ({
        id: box.id,
        label: box.label,
        position_x: Math.round(box.position.x),
        position_y: Math.round(box.position.y),
        box_width: Math.round(box.size.width),
        box_height: Math.round(box.size.height),
        color: box.color,
        updated_at: new Date().toISOString(),
      }))

      const { error } = await supabase
        .from('office_tv_group_boxes')
        .upsert(groupBoxesToUpsert, { onConflict: 'id' })

      if (error) {
        console.error('Error syncing group boxes:', error)
        // If table doesn't exist, just skip sync (migration not run yet)
        if (error.message?.includes('relation') || error.code === '42P01') {
          console.warn('office_tv_group_boxes table not found. Run SQL migration first.')
          return
        }
      }
    } catch (error) {
      console.error('Error syncing group boxes:', error)
    }
  }, 1000) // 1 second debounce
}

// Subscribe to customers changes and sync to Supabase
useCustomerTrackerStore.subscribe(
  (state) => state.customers,
  (customers) => {
    debouncedSyncCustomers(customers)
  }
)

// Subscribe to group boxes changes and sync to Supabase
useCustomerTrackerStore.subscribe(
  (state) => state.groupBoxes,
  (groupBoxes) => {
    debouncedSyncGroupBoxes(groupBoxes)
  }
)
